import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchSubscribers,
  createSubscriber as apiCreateSubscriber,
  subscribeEmail as apiSubscribeEmail,
  updateSubscriber as apiUpdateSubscriber,
  deleteSubscriber as apiDeleteSubscriber,
} from '../Email-Marketing/shared/api.js';

// Replaces the Email-Marketing `useContactsStore` zustand store.

const sample = [
  { id: 'c1', name: 'Alice Johnson', email: 'alice@example.com', tags: ['lead'], company: 'Alpha', phone: '123456', subscribed: true },
  { id: 'c2', name: 'Bob Singh', email: 'bob@example.com', tags: ['customer', 'vip'], company: 'Beta Ltd', phone: '', subscribed: true },
];

// Keys the API attaches for routing rather than for display. `table_name` matters most: it
// contains "name" and no "company", so it satisfies the substring searches below and lands a
// table name where a contact name belongs — for any row whose real name column is
// company-prefixed or missing entirely. Rows from current_allcontacts_data happen to escape it
// because their name column is inserted first, which is why this only shows up on the other two
// legacy tables.
const NON_DISPLAY_KEYS = new Set(['table_name']);

// Subscriber rows come from legacy tables with inconsistent column names, so
// the display fields are derived rather than assumed.
export const transformSubscriberRow = (row, fallbackTable) => {
  const id = row.id?.toString?.() || String(row.id);
  const keys = Object.keys(row);
  const emailKey = keys.find((k) => k.toLowerCase().includes('email'));
  const nameKey = keys.find(
    (k) =>
      !NON_DISPLAY_KEYS.has(k) &&
      k.toLowerCase().includes('name') &&
      !k.toLowerCase().includes('company')
  );

  // The company fallback needs the same treatment as the name lookup: these tables prefix their
  // columns, so an exact `company_name` only exists on some of them. Without this a telecaller
  // row with a perfectly good company name renders as "Contact 4".
  const companyKey = keys.find(
    (k) => !NON_DISPLAY_KEYS.has(k) && k.toLowerCase().includes('company')
  );

  const displayEmail = row[emailKey] || '';
  const displayName = row[nameKey] || row[companyKey] || `Contact ${id}`;
  // Surfaced on its own so the Companies view can group by it: the column is named
  // differently on every legacy table, and only this derivation knows which one it is.
  const displayCompany = (row[companyKey] || '').toString().trim();
  const tagsArray = (row.tags ? String(row.tags) : '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  return {
    ...row,
    id,
    displayEmail,
    displayName,
    displayCompany,
    tagsArray,
    email: displayEmail,
    name: displayName,
    subscribed: row.subscribed === 'Yes',
    ...(fallbackTable ? { table_name: row.table_name || fallbackTable } : {}),
  };
};

// Mutation responses use a slightly different derivation than list rows.
const transformMutationRow = (row, fallbackTable) => {
  const keys = Object.keys(row);
  const emailKey = keys.find((k) => k.toLowerCase().includes('email')) || 'email';
  const nameKey =
    keys.find((k) => k.toLowerCase() === 'name') ||
    keys.find((k) => !NON_DISPLAY_KEYS.has(k) && k.toLowerCase().includes('name'));
  return {
    ...row,
    id: row.id?.toString?.() || String(row.id),
    displayEmail: row[emailKey] || row.email || '',
    displayName: row[nameKey] || row.name || row.company || row.company_name || 'Unknown',
    displayCompany: (row[keys.find((k) => !NON_DISPLAY_KEYS.has(k) && k.toLowerCase().includes('company'))] || '').toString().trim(),
    tagsArray: (row.tags ? String(row.tags) : '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean),
    table_name: row.table_name || fallbackTable,
  };
};

export const loadContacts = createAsyncThunk(
  'emailContacts/loadContacts',
  async ({ page = 1, limit = 20, search = '', filters = {}, _retried = false } = {}, { dispatch, rejectWithValue }) => {
    try {
      // `filters` is { queryParam: 'a,b' } from the column menus. Passed through to the server
      // so the page, the total and the pagination all describe the same filtered set.
      const response = await fetchSubscribers(page, limit, search, filters);
      const transformed = response.subscribers.map((row) => transformSubscriberRow(row));

      // Deduplicate contacts by ID
      const uniqueContacts = [];
      const seenIds = new Set();
      for (const c of transformed) {
        if (c.id && !seenIds.has(c.id)) {
          seenIds.add(c.id);
          uniqueContacts.push(c);
        }
      }
      return { contacts: uniqueContacts, pagination: response.pagination };
    } catch (error) {
      console.error('Failed to load subscribers:', error);
      // Retry once behind demo auth if the session was rejected.
      const isAuthError =
        error.message?.includes('Unauthorized') || error.message?.includes('session');
      if (isAuthError && !_retried) {
        try {
          const { initDemoAuth } = await import('../Email-Marketing/shared/auth.js');
          await initDemoAuth();
          return await dispatch(
            loadContacts({ page, limit, search, filters, _retried: true })
          ).unwrap();
        } catch (authError) {
          console.error('Demo auth failed:', authError);
        }
      }
      return rejectWithValue(error.message);
    }
  }
);

export const createContact = createAsyncThunk(
  'emailContacts/createContact',
  async (contactData, { dispatch, getState }) => {
    const row = await apiCreateSubscriber(contactData);
    const transformed = transformMutationRow(row, contactData.targetTable);
    // Reload the first page so the new contact appears with an accurate count.
    const { pagination } = getState().emailContacts;
    dispatch(loadContacts({ page: 1, limit: pagination?.itemsPerPage || 20 }));
    return transformed;
  }
);

export const subscribeEmail = createAsyncThunk(
  'emailContacts/subscribeEmail',
  async (email) => {
    await apiSubscribeEmail(email);
    return email;
  }
);

export const updateContact = createAsyncThunk(
  'emailContacts/updateContact',
  async ({ id, patch }, { getState }) => {
    const current = getState().emailContacts.contacts.find((c) => c.id === id);
    const row = await apiUpdateSubscriber(id, {
      ...patch,
      targetTable: patch.targetTable || current?.table_name,
    });
    return transformMutationRow(row, current?.table_name);
  }
);

export const removeContact = createAsyncThunk(
  'emailContacts/removeContact',
  async (id, { dispatch, getState }) => {
    const state = getState().emailContacts;
    const current = state.contacts.find((c) => c.id === id);
    await apiDeleteSubscriber(id, current?.table_name);
    // Refresh the current page to get an accurate count.
    dispatch(
      loadContacts({
        page: state.pagination?.currentPage || 1,
        limit: state.pagination?.itemsPerPage || 20,
      })
    );
    return id;
  }
);

const initialState = {
  contacts: sample,
  loading: false,
  pagination: null,
  editContactPayload: null,
};

const emailContactsSlice = createSlice({
  name: 'emailContacts',
  initialState,
  reducers: {
    addContact: (state, action) => {
      state.contacts = [action.payload, ...state.contacts];
    },
    openEditDialog: (state, action) => {
      state.editContactPayload = action.payload;
    },
    clearEditDialog: (state) => {
      state.editContactPayload = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadContacts.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadContacts.fulfilled, (state, action) => {
        state.loading = false;
        state.contacts = action.payload.contacts;
        state.pagination = action.payload.pagination;
      })
      .addCase(loadContacts.rejected, (state) => {
        state.loading = false;
        state.contacts = [];
        state.pagination = null;
      })
      .addCase(updateContact.fulfilled, (state, action) => {
        state.contacts = state.contacts.map((c) =>
          c.id === action.payload.id ? action.payload : c
        );
      });
  },
});

// `editContact` was an alias of `openEditDialog` in the zustand store.
export const {
  addContact,
  openEditDialog,
  clearEditDialog,
} = emailContactsSlice.actions;
export const editContact = openEditDialog;

// Was a store getter; a plain selector now.
export const selectAllTags = (state) =>
  Array.from(new Set(state.emailContacts.contacts.flatMap((c) => c.tags || [])));

export default emailContactsSlice.reducer;
